import { useMemo, useState } from 'react';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box, ButtonBase, Collapse, Typography } from '@mui/material';

import { useVehicleInfo } from '../../hooks/useApiQueries';

const tokens = {
  white: '#FFFFFF',
  border: '#E8E8E6',
  dark: '#1C1C1E',
  textSecondary: '#666666',
  textMuted: '#888888',
  amber: '#F59E0B',
  amberDeep: '#A16207',
  amberLight: '#FEFCE8',
  amberBorder: '#FDE68A',
  red: '#EF4444',
  redDark: '#DC2626',
  redLight: '#FFF5F5',
  redBorder: '#FECACA',
  purple: '#8B5CF6',
  purpleDark: '#7C3AED',
  purpleLight: '#F5F3FF',
  purpleBorder: '#DDD6FE',
  fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

export type ActionType = 'payment' | 'documents';

interface ActionVisual {
  dotColor: string;
  badgeBg: string;
  badgeBorder: string;
  badgeColor: string;
  label: string;
}

const ACTION_VISUALS: Record<ActionType, ActionVisual> = {
  payment: {
    dotColor: tokens.purple,
    badgeBg: tokens.purpleLight,
    badgeBorder: tokens.purpleBorder,
    badgeColor: tokens.purpleDark,
    label: 'Paiement en attente',
  },
  documents: {
    dotColor: tokens.amber,
    badgeBg: tokens.amberLight,
    badgeBorder: tokens.amberBorder,
    badgeColor: tokens.amberDeep,
    label: 'Documents à fournir',
  },
};

export interface ActionItem {
  registrationId: number;
  regPlate: string;
  type: ActionType;
  description: string;
  raw: any;
}

interface ActionsRequiredAccordionProps {
  items: ActionItem[];
  onItemClick: (item: ActionItem) => void;
}

const ActionRow = ({ item, onClick, isLast }: { item: ActionItem; onClick: () => void; isLast: boolean }) => {
  const visual = ACTION_VISUALS[item.type];
  // Vrai véhicule du dossier CG résolu depuis sa plaque via l'API Plaque
  // (info_immat en base, sinon API externe). Remplace l'ancien libellé issu de
  // car_deals (autre pipeline) qui affichait un véhicule sans rapport.
  const { data: vehicleInfo } = useVehicleInfo(item.regPlate);
  const vehicleLabel = [vehicleInfo?.Marque, vehicleInfo?.Modele]
    .map((s) => (s ?? '').trim())
    .filter(Boolean)
    .join(' ');
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: '100%',
        display: 'block',
        textAlign: 'left',
        borderBottom: isLast ? 'none' : `1px solid ${tokens.border}`,
        transition: 'background-color 150ms ease',
        '&:hover': { backgroundColor: '#FAFAF9' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          padding: '18px 24px',
          minHeight: 64,
        }}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: visual.dotColor,
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", "SF Mono", monospace',
            fontSize: '14px',
            fontWeight: 600,
            color: tokens.dark,
            letterSpacing: '0.02em',
            minWidth: 110,
            flexShrink: 0,
          }}
        >
          {item.regPlate || '—'}
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '4px 12px',
            borderRadius: '999px',
            backgroundColor: visual.badgeBg,
            border: `1px solid ${visual.badgeBorder}`,
            color: visual.badgeColor,
            fontSize: '12px',
            fontWeight: 700,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {visual.label}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: '14px',
              color: tokens.textSecondary,
              fontWeight: 500,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: '1 1 auto',
            }}
          >
            {item.description}
          </Typography>
          {vehicleLabel && (
            <Typography
              sx={{
                fontSize: '14px',
                color: tokens.dark,
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: '0 1 auto',
              }}
            >
              {vehicleLabel}
            </Typography>
          )}
        </Box>
        <ChevronRightIcon sx={{ color: tokens.textMuted, fontSize: 20, flexShrink: 0 }} />
      </Box>
    </ButtonBase>
  );
};

const ActionsRequiredAccordion = ({ items, onItemClick }: ActionsRequiredAccordionProps) => {
  const [open, setOpen] = useState(true);
  const count = items.length;

  const sortedItems = useMemo(() => {
    const priority: Record<ActionType, number> = { payment: 0, documents: 1 };
    return [...items].sort((a, b) => priority[a.type] - priority[b.type]);
  }, [items]);

  if (count === 0) return null;

  return (
    <Box
      sx={{
        fontFamily: tokens.fontFamily,
        backgroundColor: tokens.white,
        border: `1px solid ${tokens.border}`,
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.04)',
        overflow: 'hidden',
        mb: 4,
      }}
    >
      <ButtonBase
        onClick={() => setOpen((v) => !v)}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 26px',
          textAlign: 'left',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <WarningAmberRoundedIcon sx={{ color: tokens.dark, fontSize: 22 }} />
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 800,
              color: tokens.dark,
              letterSpacing: '-0.01em',
            }}
          >
            Actions requises
          </Typography>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 28,
              height: 24,
              padding: '0 8px',
              borderRadius: '999px',
              backgroundColor: tokens.amberLight,
              border: `1px solid ${tokens.amberBorder}`,
              color: tokens.amberDeep,
              fontSize: '13px',
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {count}
          </Box>
        </Box>
        <ExpandMoreIcon
          sx={{
            color: tokens.textMuted,
            fontSize: 24,
            transition: 'transform 200ms ease',
            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
          }}
        />
      </ButtonBase>
      <Collapse in={open} timeout={200} unmountOnExit>
        <Box sx={{ borderTop: `1px solid ${tokens.border}` }}>
          {sortedItems.map((item, idx) => (
            <ActionRow
              key={`${item.registrationId}-${item.type}`}
              item={item}
              isLast={idx === sortedItems.length - 1}
              onClick={() => onItemClick(item)}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default ActionsRequiredAccordion;
